import { Controller, type Control } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { Tag } from '@/components/ui/Tag';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { INDUSTRIES, MAX_INVESTOR_INDUSTRIES } from '@/lib/constants/industries';
import type { InvestorProfileFormValues } from './schema';

const S = UI_STRINGS.INVESTOR_PROFILE;

interface IndustriesSectionProps {
  control: Control<InvestorProfileFormValues>;
}

/**
 * Preferred industries picker, capped at 10 (US-101 AC3), shown in the "What
 * we invest in" panel. The read view is `WhatWeInvestInCard` (MYN-217).
 */
export function IndustriesSection({ control }: IndustriesSectionProps) {
  return (
    <Card title={S.INDUSTRIES} subtitle={S.INDUSTRIES_HINT} headerDivider hoverEffect={false}>
      <Controller
        control={control}
        name="industries"
        render={({ field, fieldState }) => {
          const selected = field.value ?? [];
          const available = INDUSTRIES.filter((industry) => !selected.includes(industry));
          const atMax = selected.length >= MAX_INVESTOR_INDUSTRIES;

          return (
            <div>
              {selected.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {selected.map((industry) => (
                    <Tag key={industry} onRemove={() => field.onChange(selected.filter((i) => i !== industry))}>
                      {industry}
                    </Tag>
                  ))}
                </div>
              )}
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate">{S.ADD_INDUSTRIES}</p>
              <div className="flex flex-wrap gap-2">
                {available.map((industry) => (
                  <Tag
                    key={industry}
                    disabled={atMax}
                    onClick={() => field.onChange([...selected, industry])}
                  >
                    {industry}
                  </Tag>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate">{S.MAX_INDUSTRIES_HINT}</p>
              {fieldState.error && (
                <p role="alert" className="mt-1 text-xs text-danger">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          );
        }}
      />
    </Card>
  );
}
